import useFetch from "@/hooks/use-fetch";
import { extractMessageFromError } from "@/shared/notification";
import { BaseQuery, QueryResponse } from "@/shared/types/base";
import { Autocomplete, AutocompleteCloseReason, CircularProgress, debounce, SxProps, TextField, Theme } from "@mui/material";
import { forwardRef, ReactNode, Ref, useEffect, useRef, useState } from "react";

export type ModelId = string;
export type Model = { id: ModelId } & Record<string, any>
export type GetFunction<T extends Model> = (id: ModelId) => Promise<T>
export type GetAllFunction<T extends Model, P extends BaseQuery> = (filter: P, count: number) => Promise<T[] | QueryResponse<T>>

export interface ModelComboBoxPropsAsync<T extends Model, P extends BaseQuery> {
    value?: ModelId | "",
    setValue?: (value: ModelId | "") => void,
    onValueChange?: (value: ModelId | "") => void,

    get: (id: ModelId) => Promise<T>,
    getAll: (filter: P, count: number) => Promise<T[] | QueryResponse<T>>,
    labelName?: keyof T,
    renderOption?: (option: T) => ReactNode,
    optionCount?: number,

    label?: ReactNode,
    disabled?: boolean,
    size?: "medium" | "small",
    sx?: SxProps<Theme>,
    error?: boolean,
    helperText?: string,
    onBlur?: React.FocusEventHandler<HTMLInputElement>,
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>,
    onClose?: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void
}



const ModelComboBoxAsync = forwardRef(<T extends Model, P extends BaseQuery>({
    get,
    getAll,

    value,
    setValue = () => { },
    labelName = "nombre",
    renderOption = (o) => o[labelName],
    optionCount = 10,

    sx,
    disabled,
    size,
    onClose,
    onValueChange,
    ...props
}: ModelComboBoxPropsAsync<T, P>, ref: any) => {
    const [_initialLoading, _setInitialLoading] = useState(true);
    const [_value, _setValue] = useState<T | null>(null);

    const [options, setOptions] = useState<T[]>([]);
    const [page, setPage] = useState(0);
    const [inputValue, setInputValue] = useState("");
    const [hasMore, setHasMore] = useState(true);
    const [loadingOptions, setLoadingOptions] = useState(false);



    const { fetch: _initialValue, loading: _valueLoading, error: _valueError } = useFetch(async (value: ModelId) => {
        if (value) return await get(value);
        return null;
    }, {
        onSuccess: v => {
            _setInitialLoading(false);
            _setValue(v)
        },
        onError: () => {
            _setInitialLoading(false);
        }
    });

    const fetchOptions = async (input: string, pageToFetch: number, append = false) => {
        setLoadingOptions(true);
        const result = await getAll({ [labelName]: input, pageSize: optionCount, page: pageToFetch } as any, optionCount);
        let newOptions: T[] = [];
        if ("result" in result) {
            newOptions = result.result;
            const totalPages = Math.floor(result.count / result.pageSize) + (result.count % result.pageSize !== 0 ? 1 : 0);
            setHasMore(result.page < totalPages - 1);
        } else {
            newOptions = result;
            setHasMore(newOptions.length === optionCount);
        }

        setOptions(prev => append ? [...prev, ...newOptions] : newOptions);
        setLoadingOptions(false);
    };

    useEffect(() => {
        _initialValue(value || "");
        setOptions([]);
        setPage(0);
        setInputValue("");
        fetchOptions("", 0, false);
    }, [value]);

    //debounce, buscar opciones
    const handleInputChange = (_: any, v: string) => {
        setInputValue(v);
        debouncedSearch(v);
    };
    const debouncedSearch = useRef(
        debounce((searchTerm: string) => {
            setPage(0);
            fetchOptions(searchTerm, 0, false);
        }, 500)
    ).current;

    //offset, traer mas opciones
    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const listboxNode = event.currentTarget;
        if (
            hasMore &&
            !loadingOptions &&
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 // allow small offset
        ) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchOptions(inputValue, nextPage, true);
        }
    };

    // loader final
    const displayOptions = loadingOptions && hasMore ? [...options, { id: 'loading', nombre: 'Cargando...' } as unknown as T] : options;

    return <Autocomplete
        options={displayOptions}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        noOptionsText="Ninguna Opción"
        renderOption={({ key, ...props }, option) => {
            if (option.id === 'loading') {
                return (
                    <li key={key} {...props} style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                        <CircularProgress size={20} />
                    </li>
                );
            }
            return <li key={key} {...props}>{renderOption(option)}</li>
        }}
        renderInput={(params) => <TextField
            {...props}
            {...params}
            inputRef={ref}
            slotProps={{
                input: {
                    ...params.InputProps,
                    endAdornment: <>
                        {(_initialLoading || _valueLoading || loadingOptions) && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                    </>
                },
            }}
        />}
        onInputChange={handleInputChange}
        slotProps={{
            listbox: {
                sx: { maxHeight: "300px" }, // aca se cambia la altura al abrir el dropdown, para la paginacion
                onScroll: handleScroll
            }
        }}
        value={_value}
        onChange={(_, v) => {
            // no elegirl loader
            if (v && v.id === 'loading') return;

            _setValue(v);
            if (v) {
                setValue(v.id);
                onValueChange?.(v.id);
            } else {
                setValue("");
                onValueChange?.("");
            }
        }}
        onClose={onClose}
        size={size}
        autoSelect
        loading={_initialLoading || _valueLoading || loadingOptions}
        loadingText={_valueError ? extractMessageFromError(_valueError) : "Cargando..."}
        disabled={disabled || _initialLoading}
        getOptionLabel={o => labelName in o ? String(o[labelName]) : o.id}
        sx={sx}
    />
})

export default ModelComboBoxAsync as <T extends Model, P extends BaseQuery>(props: ModelComboBoxPropsAsync<T, P> & { ref: Ref<HTMLInputElement> }) => ReactNode;